/**
 * Anklickbare Verknuepfungen (Roadmap F-11, Leitlinie 5).
 *
 * Ein Link auf einen Bereich fuehrt seit F-01 zu dessen Board. `useFokus`
 * (`?fokus=<id>` hebt einen Eintrag einer Liste kurz hervor) bleibt fuer
 * Listen erhalten.
 *
 * Mit den Detailseiten aendern sich nur die Pfade hier - alle Links in der App
 * folgen automatisch. Projekte haben ihre seit F-10, Ziele seit F-12.
 *
 * Kein Bereichsfilter kann das Ziel eines Links verstecken: nach der
 * Verknuepfungsregel (F-11) teilen verknuepfte Eintraege ihren Bereich. Wer
 * die Quelle sieht, sieht auch das Ziel.
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ID } from '@/domain/types';

export const pfad = {
  /** Seit F-12 die eigene Detailseite des Ziels. */
  ziel: (id: ID) => `/ziele/${encodeURIComponent(id)}`,
  /** Seit F-10 die eigene Detailseite des Projekts. */
  projekt: (id: ID) => `/projekte/${encodeURIComponent(id)}`,
  /** Seit F-01 das Board des Bereichs. */
  bereich: (id: ID) => `/bereiche/${encodeURIComponent(id)}`,
  /** Fach-Detailseite der Schule (F-22 Stufe 8). */
  fach: (id: ID) => `/schule/fach/${encodeURIComponent(id)}`,
  wissen: (id: ID) => `/wissen/${encodeURIComponent(id)}`,
};

/** Wie lange der Akzentrahmen sichtbar bleibt. */
const FOKUS_MS = 2500;

/** Klassen fuer den hervorgehobenen Eintrag. */
export const FOKUS_KLASSE = 'ring-2 ring-accent ring-offset-2 ring-offset-canvas';

/**
 * Die ID des gerade hervorgehobenen Eintrags, oder `null`.
 *
 * Der Parameter wird nach kurzer Zeit aus der Adresse entfernt, damit der
 * Rahmen verschwindet und ein Neuladen ihn nicht wieder anzeigt.
 */
export function useFokus(): ID | null {
  const [params, setParams] = useSearchParams();
  const fokus = params.get('fokus');

  useEffect(() => {
    if (!fokus) return;
    const timer = setTimeout(() => {
      setParams((p) => { p.delete('fokus'); return p; }, { replace: true });
    }, FOKUS_MS);
    return () => clearTimeout(timer);
  }, [fokus, setParams]);

  return fokus;
}

/** Scrollt das Element in die Mitte, sobald es hervorgehoben wird. */
export function useFokusRef<T extends HTMLElement>(aktiv: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (aktiv) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [aktiv]);
  return ref;
}
